import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AdminUser, AdminRole, LoginForm } from '../types'
import { unifiedAdminAuth } from '../services/unified-admin-auth'

interface AuthState {
  user: AdminUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (credentials: LoginForm) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  clearError: () => void
  checkPermission: (permission: string) => boolean
  hasRole: (role: AdminRole) => boolean
  verifyMagicLink: (token: string) => Promise<{ success: boolean; user?: AdminUser }>
  initializeAuth: () => Promise<void>
}

// Note: Role permissions are now centrally managed in user-adapter.ts

// Global initialization state to prevent race conditions
let isInitializing = false
let initializationPromise: Promise<void> | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginForm) => {
        set({ isLoading: true, error: null })
        
        try {
          // For unified auth, we need to handle magic link flow
          // This will request a magic link to be sent to the user's email
          const email = credentials.username.includes('@') ? credentials.username : `${credentials.username}@virtualrevamp.com`
          
          const result = await unifiedAdminAuth.requestMagicLink(email)
          
          if (result.success) {
            set({
              isLoading: false,
              error: null
            })
            // Don't set authenticated yet - user needs to click magic link
            throw new Error('Please check your email and click the magic link to complete login')
          } else {
            throw new Error(result.error || 'Failed to send magic link')
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Login failed'
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage
          })
          throw error
        }
      },

      logout: () => {
        // Use unified auth logout
        unifiedAdminAuth.logout()
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        })
      },

      refreshToken: async () => {
        // Prevent multiple concurrent refresh attempts
        if ((get() as any)._refreshing) {
          console.log('🔄 Token refresh already in progress, skipping...')
          return
        }
        
        // Set refreshing flag
        set({ ...(get() as any), _refreshing: true })
        
        try {
          const { token } = get()
          if (!token) {
            // Try development auth if no token
            const isDevelopment = (import.meta as any).env?.VITE_NODE_ENV === 'development' || 
                                 (import.meta as any).env?.DEV || 
                                 window.location.hostname === 'localhost'
            
            if (isDevelopment) {
              try {
                console.log('🔧 Attempting development token refresh...')
                const response = await fetch('/api/auth/dev/admin-token', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' }
                })
                
                if (response.ok) {
                  const data = await response.json()
                  console.log('✅ Development token obtained, setting auth state...')
                  
                  // Set token in unified auth service first
                  unifiedAdminAuth.setAuthToken(data.access_token)
                  
                  const user = {
                    id: data.user.id,
                    email: data.user.email,
                    username: data.user.email,
                    role: data.user.admin_role || 'super_admin',
                    mfa_enabled: false,
                    created_at: new Date().toISOString(),
                    last_login: new Date().toISOString()
                  }
                  
                  // The unified auth service will notify the store via onAuthStateChange
                  // But we also set it directly to ensure immediate update
                  set({
                    user,
                    token: data.access_token,
                    isAuthenticated: true,
                    error: null
                  })
                  
                  console.log('✅ Development auth state set successfully')
                  return
                } else {
                  console.warn('⚠️ Development token request failed:', response.status)
                }
              } catch (error) {
                console.error('❌ Development token refresh failed:', error)
              }
            }
            return
          }
          
          try {
            console.log('🔄 Attempting regular token refresh...')
            const result = await unifiedAdminAuth.refreshToken()
            const { token: newToken, user } = result
            
            console.log('✅ Token refresh successful')
            set({
              user,
              token: newToken,
              isAuthenticated: true,
              error: null
            })
          } catch (error) {
            console.error('❌ Token refresh failed:', error)
            // Token refresh failed, logout user
            get().logout()
          }
        } finally {
          // Clear refreshing flag
          set({ ...(get() as any), _refreshing: false })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      checkPermission: (permission: string) => {
        return unifiedAdminAuth.checkPermission(permission)
      },

      hasRole: (role: AdminRole) => {
        return unifiedAdminAuth.hasRole(role)
      },

      // New method for magic link verification
      verifyMagicLink: async (token: string) => {
        set({ isLoading: true, error: null })
        
        try {
          console.log('🔍 Auth Store: Starting magic link verification for token:', token.substring(0, 10) + '...')
          
          const result = await unifiedAdminAuth.verifyMagicLink(token)
          
          console.log('🔍 Auth Store: Verification result:', { 
            success: result.success, 
            hasUser: !!result.user,
            error: result.error 
          })
          
          if (result.success && result.user) {
            const user = result.user
            const accessToken = unifiedAdminAuth.getAccessToken()
            
            console.log('🔍 Auth Store: Setting auth state:', {
              userId: user.id,
              userEmail: user.email,
              userRole: user.role,
              hasToken: !!accessToken,
              tokenLength: accessToken?.length
            })
            
            set({
              user,
              token: accessToken,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
            
            console.log('✅ Auth Store: Authentication state updated successfully')
            return { success: true, user }
          } else {
            console.error('❌ Auth Store: Verification failed:', result.error)
            throw new Error(result.error || 'Magic link verification failed')
          }
        } catch (error: any) {
          const errorMessage = error.message || 'Magic link verification failed'
          console.error('❌ Auth Store: Exception during verification:', error)
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage
          })
          throw error
        }
      },

      // Initialize authentication on app startup
      initializeAuth: async () => {
        // Prevent multiple concurrent initializations
        if (isInitializing || initializationPromise) {
          console.log('🔄 Auth initialization already in progress, waiting...')
          return initializationPromise || Promise.resolve()
        }

        isInitializing = true
        initializationPromise = (async () => {
          try {
            console.log('🚀 Starting auth initialization...')
            
            const { token, isAuthenticated } = get()
            
            // If already authenticated, validate the token
            if (token && isAuthenticated) {
              try {
                const isValid = await unifiedAdminAuth.isAuthenticated()
                if (!isValid) {
                  console.log('🔄 Stored token is invalid, attempting refresh...')
                  await get().refreshToken()
                } else {
                  console.log('✅ Auth initialization: Token is valid, no refresh needed')
                }
              } catch (error) {
                console.error('Token validation failed:', error)
                try {
                  await get().refreshToken()
                } catch (refreshError) {
                  console.error('Token refresh also failed:', refreshError)
                  // Clear invalid auth state
                  set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    error: 'Authentication failed'
                  })
                }
              }
            } else {
              // No token, try to get one in development mode
              console.log('🔄 No authentication found, attempting refresh...')
              await get().refreshToken()
            }
            
            console.log('✅ Auth initialization completed')
          } catch (error) {
            console.error('❌ Auth initialization failed:', error)
            set({ 
              error: 'Authentication initialization failed',
              isLoading: false
            })
          } finally {
            isInitializing = false
            initializationPromise = null
            console.log('🏁 Auth initialization cleanup completed')
          }
        })()
        
        return initializationPromise
      }
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      }),
      onRehydrateStorage: () => (state) => {
        // Set token in unified auth when rehydrating
        if (state?.token) {
          unifiedAdminAuth.setAuthToken(state.token)
        }
        
        // Subscribe to unified auth state changes for real-time synchronization
        const unsubscribe = unifiedAdminAuth.onAuthStateChange((user, token) => {
          console.log('🔄 Unified auth state changed:', { 
            hasUser: !!user, 
            hasToken: !!token,
            userEmail: user?.email 
          })
          
          const currentState = useAuthStore.getState()
          
          // Only update if state actually changed to prevent loops
          if (currentState.user?.id !== user?.id || currentState.token !== token) {
            console.log('📝 Updating auth store from unified auth service')
            useAuthStore.setState({
              user,
              token,
              isAuthenticated: !!(user && token),
              error: null
            })
          }
        })
        
        // Store unsubscribe function globally for cleanup if needed
        if (typeof window !== 'undefined') {
          (window as any).__authStateUnsubscribe = unsubscribe
        }
      }
    }
  )
)

// Auto-refresh token every 30 minutes
setInterval(() => {
  const { isAuthenticated, refreshToken } = useAuthStore.getState()
  if (isAuthenticated) {
    refreshToken().catch(() => {
      // Refresh failed, user will be logged out
    })
  }
}, 30 * 60 * 1000) // 30 minutes